import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SidebarList, Notification } from 'app/models';
import { AuthenticationService, OutletService } from 'app/services';
import { OutletGroups } from 'app/interfaces';
import { selectableOutletGroups } from 'app/services/outlet/outlet-group-options';
import * as actions from 'redux/actions';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sidebar-entity-table',
  templateUrl: './sidebar-entity-table.component.html',
  styleUrls: ['./sidebar-entity-table.component.scss']
})
export class SidebarEntityTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: SidebarList[] = [];
  @Input() allItems: SidebarList[];
  @Input() kind: 'author' | 'outlet' | 'topic' | 'article' = 'topic';
  @Input() showCompetitorAction = true;
  @Output() selected = new EventEmitter<any>();
  @Output() create = new EventEmitter<void>();
  @Output() unfollow = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();

  readonly pageSize = 20;
  rows: any[] = [];
  visibleRows: any[] = [];
  nameFilter = '';
  categoryFilter = '';
  groupFilter = '';
  categoryOptions: string[] = [];
  groupOptions: string[] = [];
  outletGroupOptions: OutletGroups[] = [];
  filterOpen: { [key: string]: boolean } = {};
  first = 0;
  project: any;
  categories: any[] = [];
  private outletGroups: OutletGroups[] = [];
  private subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    private outletService: OutletService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit() {
    this.subscriptions.add(this.store.select('project').subscribe(({ projectSelected }) => {
      this.project = projectSelected;
    }));
    this.subscriptions.add(this.store.select('category').subscribe(({ categories }) => {
      this.categories = categories || [];
    }));
    if (this.kind === 'outlet') {
      this.subscriptions.add(this.store.select('advancedSearch').subscribe(({ outletGroups }) => {
        if (outletGroups && outletGroups.length) {
          this.outletGroups = outletGroups;
          this.buildRows();
        }
      }));
      if (!this.outletGroups.length) {
        this.subscriptions.add(this.outletService.getOutletGroups().subscribe(
          groups => {
            this.outletGroups = groups || [];
            this.store.dispatch(actions.setOutletGroups({ outletGroups: this.outletGroups }));
          },
          () => {}
        ));
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items || changes.allItems || changes.kind) {
      this.first = 0;
      this.buildRows();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  resetPage() {
    this.first = 0;
    this.applyFilters();
  }

  toggleFilter(field: string, event: Event) {
    event.stopPropagation();
    this.filterOpen[field] = !this.filterOpen[field];
    if (!this.filterOpen[field]) {
      if (field === 'name') { this.nameFilter = ''; }
      if (field === 'category') { this.categoryFilter = ''; }
      if (field === 'groups') { this.groupFilter = ''; }
      this.resetPage();
    }
  }

  getGroupNames(row: any): string[] {
    return row.groupNames || [];
  }

  private groupName(group: any): string {
    if (group && typeof group === 'object') { return group.name || ''; }
    const match = this.outletGroups.find(value => String(value.id) === String(group));
    return match ? match.name : typeof group === 'string' ? group : '';
  }

  selectRow(row: any) {
    this.store.dispatch(actions.hideSidebar());
    if (this.kind === 'article') {
      if (row.item.url) { window.open(row.item.url, '_blank', 'noopener'); }
    } else {
      this.selected.emit(row.item);
    }
  }

  private applyFilters() {
    const name = this.nameFilter.trim().toLowerCase();
    const category = this.categoryFilter.trim().toLowerCase();
    const group = this.groupFilter.trim().toLowerCase();
    this.visibleRows = this.rows.filter(row =>
      (this.categoryFilter === 'Ignore' || row.category !== 'Ignore') &&
      (!name || row.name.toLowerCase().includes(name)) &&
      (!category || row.category === this.categoryFilter) &&
      (!group || (this.kind === 'outlet'
        ? row.groupIds.some(id => String(id) === this.groupFilter)
        : group === '__none__' ? !this.getGroupNames(row).length
          : this.getGroupNames(row).includes(this.groupFilter)))
    );
  }

  toggleFollow(row: any) {
    if (this.kind === 'article') {
      this.unfollow.emit(row.item);
      return;
    }
    if (!this.project) { return; }
    const item = row.item;
    if (this.kind === 'author') {
      this.store.dispatch(actions.deleteAuthor({ payload: { id: item.partyId, name: item.name, userProject: this.project } }));
    } else if (this.kind === 'outlet') {
      this.store.dispatch(actions.deleteOutlet({ payload: { id: item.partyId, name: item.name, userProject: this.project } }));
    } else {
      this.store.dispatch(actions.deleteTopic({
        topicId: item.userTopicId, userId: null, userProject: this.project
      }));
    }
  }

  toggleLabel(row: any, label: string) {
    if (!this.project) { return; }
    const item = row.item;
    const category = this.categories.find(value => String(value.name).toLowerCase() === label.toLowerCase());
    if (!category) {
      this.store.dispatch(actions.showNotification({
        notify: new Notification(`The ${label} label is not available`, 'error', 'br')
      }));
      return;
    }
    const categoryId = row.category === label ? null : category.id;
    if (this.kind === 'author') {
      this.store.dispatch(actions.updateAuthorLabel({
        userPartyId: item.id, userProject: this.project, author: { ...item, id: item.partyId }, userLabelId: categoryId
      }));
    } else if (this.kind === 'outlet') {
      this.store.dispatch(actions.updateOutletLabel({
        userPartyId: item.id, userProject: this.project, outlet: { ...item, id: item.partyId }, userLabelId: categoryId
      }));
    } else {
      this.store.dispatch(actions.updateTopic({
        topic: { ...item, categoryId },
        userTopicId: item.userTopicId, userId: null, userProject: this.project
      }));
    }
  }

  private buildRows() {
    const sourceItems = this.allItems
      ? [...(this.items || []), ...this.allItems.filter(item => item.subTitle === 'Ignore')]
      : this.items || [];
    this.rows = sourceItems.map(entry => {
      const item = entry.parentObject || {};
      const stats = item.myCompanyStats || {};
      const groupIds = this.kind === 'outlet' && Array.isArray(item.groupIds) ? item.groupIds : [];
      const groups = this.kind === 'article'
        ? item.outlet || entry.subTitle
        : this.kind === 'outlet' ? groupIds
        : item.outletGroups || item.outletGroupIds || item.groups || item.outlets || item.topicOutlets || item.outletNames ||
          (this.kind === 'author' ? stats.outletNames : null);
      const groupNames = Array.isArray(groups)
        ? groups.map(group => this.groupName(group)).filter(Boolean)
        : typeof groups === 'string' ? groups.split(',').map(group => this.groupName(group.trim())).filter(Boolean)
          : [];
      return {
        item,
        name: entry.title || item.name || '',
        category: item.categoryName || item.label?.name || '',
        groups: groupNames.join(', '),
        groupNames,
        groupIds,
        id: item.partyId || item.id,
        date: item.publishDate || entry.publishDate || null,
        count: stats.numItems != null ? stats.numItems : (item.myCompanyItems ?? 0),
        recent: stats.maxDate || item.myCompanyMostRecent || null
      };
    });
    this.categoryOptions = Array.from(new Set([
      ...this.rows.map(row => row.category).filter(Boolean),
      'Ignore'
    ])).sort();
    const allGroups = this.rows.reduce(
      (names: string[], row: any) => names.concat(row.groupNames as string[]),
      [] as string[]
    );
    this.groupOptions = Array.from(new Set<string>(allGroups)).sort();
    this.outletGroupOptions = selectableOutletGroups(this.outletGroups, this.authenticationService.isAdminUser());
    this.applyFilters();
  }

}
